<template>
  <div v-if="model != null">
    <div class="content-area__content" v-if="model.isMFAChallenge">
      <vue-title :title="$t('identityProvider:fraudControl:challenge:title')"></vue-title>
      <h1 class="section-title">{{ $t('identityProvider:fraudControl:challenge:title') }}</h1>

      <Form v-slot="{ validate, setErrors, setFieldError }">
        <form ref="form" action="forcechallenge/index" @submit.prevent="validateBeforeSubmit(validate, setErrors, setFieldError)">
          <!-- Server summary -->
          <div class="validation-summary-errors alert alert-danger fade show"
               role="alert"
               data-valmsg-summary="true"
               data-test-element="idp-alert-danger-secureNow-challenge"
               v-if="model.showError && !hasAnyFieldErrors">
            <i class="material-icons">warning</i>
            <span v-if="model.errorMessage">{{ model.errorMessage }}</span>
            <span v-else class="alert-message" data-test-element="alert-message">{{ $t('validation:summary') }}</span>
          </div>

          <!-- Client/server validation summary -->
          <div class="validation-summary-errors alert alert-danger fade show"
               role="alert"
               data-valmsg-summary="true"
               v-if="hasAnyFieldErrors">
            <i class="material-icons">warning</i>
            <span v-if="formSummaryMessage">{{ formSummaryMessage }}</span>
            <span v-else class="alert-message" data-test-element="alert-message">{{ $t('validation:summary') }}</span>
          </div>

          <fieldset class="panel">
            <div class="panel-message">
              {{ $t('identityProvider:fraudControl:challenge:instructions') }}
            </div>
            <div>
              <div class="form-group">
                <label data-test-element="challenge-question">
                  <span class="label -inline">{{ $t('identityProvider:fraudControl:challenge:prompt') }}</span>
                  {{ model.prompt }}
                </label>
              </div>

              <div class="form-group">
                <label class="security-answer">
                  <span class="label">{{ $t('identityProvider:fraudControl:challenge:answer') }}</span>

                  <Field name="Answer" :rules="{ required: true }" v-model="model.answer" v-slot="{ field, meta }">
                    <input type="password"
                           v-bind="field"
                           name="Answer"
                           ref="answer"
                           data-test-element="Answer"
                           class="control"
                           v-focus
                           autocomplete="off"
                           :class="{ 'input-validation-error': submitted && !meta.valid }" />
                  </Field>

                  <ErrorMessage v-if="submitted" name="Answer" v-slot="{ message }">
                    <span class="field-validation-error">{{ message }}</span>
                  </ErrorMessage>
                </label>

                <label class="show-answer checkbox">
                  <input type="checkbox" data-test-element="checkbox" @change="changeVisibility" />
                  <i class="material-icons checkbox-icon"></i>
                  {{ $t('shared:action:showAnswer') }}
                </label>
              </div>

              <div class="form-actions">
                <button type="submit" name="Action:SecurityQuestion" data-test-element="submitBtn" class="button" :disabled="disabled">
                  {{ $t('shared:action:continue') }}
                </button>
                <a @click.prevent="cancelChallenge" href="forcechallenge/cancel" class="link link-text-transform button-secondary">
                  {{ $t('shared:action:cancel') }}
                </a>
              </div>
            </div>
          </fieldset>
        </form>
      </Form>
    </div>
    <div v-else>
      <selectphones-component />
    </div>
  </div>
</template>

<style>
</style>

<script>
  import redirectHandler from 'utils/redirect';
  import { Form, Field, ErrorMessage } from 'vee-validate';
  import { applyServerErrorsFromAxios, focusFirstInvalid } from 'utils/validation-mixin';
  import selectphonescomponent from './selectphones';

  export default {
    name: 'ForceChallenge',
    components: {
      'selectphones-component': selectphonescomponent,
      Form,
      Field,
      ErrorMessage
    },

    data: () => ({
      model: null,
      disabled: false,
      submitted: false,
      hasAnyFieldErrors: false,
      formSummaryMessage: null
    }),

    methods: {
      async changeVisibility(event) {
        const el = this.$refs.answer;
        if (el) {
          el.type = event.target.checked ? 'text' : 'password';
        }
      },

      async validateBeforeSubmit(validate, setErrors, setFieldError) {
        this.formSummaryMessage = null;
        this.hasAnyFieldErrors = false;
        this.submitted = true;

        const result = await validate();
        const isValid = result === true || result?.valid === true;

        if (isValid) {
          await this.post(setErrors, setFieldError);
        } else {
          this.hasAnyFieldErrors = true;
          focusFirstInvalid(this.$refs.form);
        }
      },

      async post(setErrors, setFieldError) {
        const form = this.$refs.form;
        this.disabled = true;
        try {
          const response = await this.$http.post(form.action, this.model);
          redirectHandler.checkAndRedirect(response, this.$router);
        } catch (error) {
          const resp = error?.response;

          // If there's a new prompt, the user has a new question to answer.
          if (resp?.data && resp.data.prompt) {
            this.model = resp.data;
            return;
          }

          // Map ASP.NET Core 400 ModelState to fields and summary
          if (applyServerErrorsFromAxios(error, { setErrors, setFieldError })) {
            const bag = resp?.data?.errors || resp?.data;
            const summary = bag && (bag[''] || bag['.']);
            const firstSummary = Array.isArray(summary) ? summary.find(Boolean) : (summary || null);
            this.formSummaryMessage = firstSummary || null;
            this.hasAnyFieldErrors = true;
            focusFirstInvalid(this.$refs.form);
            return;
          }

          throw error;
        } finally {
          this.disabled = false;
        }
      },

      async cancelChallenge(event) {
        const response = await this.$http.post(event.target.href);
        redirectHandler.checkAndRedirect(response, this.$router);
      }
    },

    async created() {
      const returnUrl = this.$route.query['returnUrl'];
      const cancelLocation = this.$route.query['cancelLocation'];
      const form = 'forcechallenge/index';

      try {
        const response = await this.$http.get(form, {
          params: {
            returnUrl: returnUrl,
            cancelLocation: cancelLocation
          }
        });
        this.model = response.data;
      } catch (error) {
        throw error;
      }
    }
  };
</script>